import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { DateRangeField } from './DateRangeField';
import { getErrorMessage } from '../api/client';
import { getReceiptCostEstimate, getSalesCostEstimate } from '../api/inventory';
import { useAuth } from '../auth/AuthProvider';
import { getFinancialStoreOptions } from '../auth/permissions';
import type {
  ReceiptCostEstimateDetail,
  RecipeCoverageFilters,
  SalesCostEstimateResponse,
} from '../types/inventory';
import { AccessDeniedPage } from '../pages/AccessDeniedPage';
import { DEFAULT_STORE_TIMEZONE, resolveStoreTimezone, dateToStoreISO } from '../utils/timezones';
import { useCardRows } from '../utils/dataTableCards';
import { useLoyversePresence } from '../utils/loyversePresence';
import type { AuthStore } from '../types/auth';
import { Modal } from './Modal';
import {
  CostingSources,
  formatMoney,
  formatOptionalMoney,
  formatOptionalSignedMoney,
  formatQuantity,
  RealCostBadge,
  RecipeStatusBadge,
} from '../utils/costFormatting';

const PAGE_SIZE = 25;

export function SalesCostEstimatePanel() {
  const { user, stores: authStores } = useAuth();
  // Patrón de tarjetas en móvil (≤640 px) para la tabla de costo por producto.
  const cards = useCardRows();
  // ZTV-03/ZTV-04: este análisis cubre SOLO ventas conectadas desde Loyverse — sin la integración,
  // se declara el alcance en vez de mostrar ceros engañosos.
  const loyverseConnected = useLoyversePresence();
  // Fase 16B-2 (brecha 2 de PERMISSIONS_GAPS.md): costo/margen estimado es información financiera.
  const financialStores = getFinancialStoreOptions(user?.role, authStores);
  const preselectedStoreId = !financialStores.allowsAllStores && financialStores.selectableStores.length === 1
    ? financialStores.selectableStores[0]!.id
    : undefined;
  const [draft, setDraft] = useState<RecipeCoverageFilters>(() => defaultFilters(preselectedStoreId));
  const [filters, setFilters] = useState<RecipeCoverageFilters>(() => defaultFilters(preselectedStoreId));
  const [result, setResult] = useState<SalesCostEstimateResponse>();
  const [detail, setDetail] = useState<ReceiptCostEstimateDetail>();
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [detailError, setDetailError] = useState<string>();

  const loadEstimate = useCallback(async (nextFilters: RecipeCoverageFilters, offset = 0) => {
    setIsLoading(true);
    setError(undefined);
    try {
      const response = await getSalesCostEstimate({ ...nextFilters, limit: PAGE_SIZE, offset });
      setResult(response);
      setFilters(nextFilters);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible calcular el costo estimado.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (financialStores.hasNoFinancialAccess) {
      setIsLoading(false);
      return;
    }
    void loadEstimate(defaultFilters(preselectedStoreId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEstimate]);

  if (financialStores.hasNoFinancialAccess) {
    return <AccessDeniedPage
      message="No tienes permiso para ver información financiera. Pídele al Dueño o a un Gerente que revise tus tiendas asignadas."
      onReturn={() => { window.location.hash = 'dashboard'; }}
      title="No tienes permiso para ver el costo estimado de ventas"
    />;
  }

  if (loyverseConnected === false) {
    return <div className="coverage-panel"><div className="empty-panel">
      <strong>Esta vista analiza las ventas conectadas desde Loyverse</strong>
      <span>Tu negocio no tiene esa integración conectada. El consumo y margen de las ventas registradas en Negocio360 vive en Ventas e ingresos → Consumo y margen.</span>
    </div></div>;
  }

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    void loadEstimate(draft);
  };

  const openReceipt = async (receiptId: string) => {
    if (isDetailLoading) return;
    setDetail(undefined);
    setDetailError(undefined);
    setIsDetailLoading(true);
    try {
      const response = await getReceiptCostEstimate(receiptId);
      setDetail(response.receipt);
    } catch (loadError) {
      setDetailError(getErrorMessage(loadError, 'No fue posible cargar el costo del ticket.'));
    } finally {
      setIsDetailLoading(false);
    }
  };

  const summary = result?.summary;

  return (
    <div className="coverage-panel sales-cost-panel">
      <div className="cost-disclaimer">Este costo es estimado. La utilidad real dependerá de compras, stock, merma y ajustes. Este reporte no modifica inventario.</div>
      {error && <div className="alert alert--error">{error}</div>}

      <form className="coverage-filters" onSubmit={applyFilters}>
        <DateRangeField from={draft.dateFrom || ''} to={draft.dateTo || ''} onChange={(from, to) => setDraft({ ...draft, dateFrom: from, dateTo: to })} />
        <label className="field"><span>Tienda</span><select value={draft.storeId || ''} onChange={(event) => setDraft({ ...draft, storeId: event.target.value || undefined })}>{financialStores.allowsAllStores && <option value="">Todas las tiendas</option>}{!financialStores.allowsAllStores && !draft.storeId && <option value="">Selecciona una tienda</option>}{financialStores.selectableStores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
        <label className="field"><span>Buscar</span><input placeholder="Producto, variante o SKU" value={draft.search || ''} onChange={(event) => setDraft({ ...draft, search: event.target.value || undefined })} /></label>
        <label className="coverage-check"><input checked={Boolean(draft.onlyMissingRecipe)} type="checkbox" onChange={(event) => setDraft({ ...draft, onlyMissingRecipe: event.target.checked, ...(event.target.checked ? { onlyIncompleteCost: false } : {}) })} /><span>Solo sin receta</span></label>
        <label className="coverage-check"><input checked={Boolean(draft.onlyIncompleteCost)} type="checkbox" onChange={(event) => setDraft({ ...draft, onlyIncompleteCost: event.target.checked, ...(event.target.checked ? { onlyMissingRecipe: false } : {}) })} /><span>Solo costo incompleto</span></label>
        <button className="button button--primary" disabled={isLoading || (!financialStores.allowsAllStores && !draft.storeId)} type="submit">{isLoading ? 'Calculando...' : 'Aplicar filtros'}</button>
      </form>
      {!financialStores.allowsAllStores && !draft.storeId && <div className="alert alert--warning">Selecciona una tienda válida para ver su información financiera.</div>}

      {summary && <div className="coverage-summary cost-summary">
        <SummaryCard label="Ventas brutas" value={formatMoney(summary.grossSales)} />
        <SummaryCard label="Costo estimado" help="Basado en el precio de compra de referencia de cada insumo." value={formatOptionalMoney(summary.estimatedCost)} />
        <SummaryCard label="Margen estimado" help="Venta menos costo estimado." value={formatOptionalMoney(summary.estimatedGrossMargin)} />
        <SummaryCard label="Costo real promedio" help="Basado en stock y compras confirmadas de la tienda." value={formatOptionalMoney(summary.realAverageCost)} />
        <SummaryCard label="Margen real promedio" help="Venta menos costo real promedio." value={formatOptionalMoney(summary.realAverageGrossMargin)} />
        <SummaryCard label="Margen estimado %" value={summary.estimatedGrossMarginPercent === null ? 'No disponible' : `${summary.estimatedGrossMarginPercent}%`} />
        <SummaryCard label="Cobertura costeada" value={`${summary.costedCoveragePercentBySales}%`} />
        <SummaryCard label="Líneas sin receta" value={summary.missingRecipeLinesCount} />
        <SummaryCard label="Costo incompleto" value={summary.incompleteCostLinesCount} />
      </div>}

      {summary && summary.soldLinesCount > 0 && summary.completeCostLinesCount < summary.soldLinesCount && <p className="cost-scope-note">Costo y margen corresponden solo a ventas completamente costeadas: {summary.completeCostLinesCount} de {summary.soldLinesCount} líneas.</p>}

      {isLoading ? <div className="inline-loading"><span className="spinner" />Calculando costos...</div> : result && result.items.length > 0 ? (
        <>
          {/* Owner Intelligence v1 (spec §17, criterio A7): 1450 px de tabla no caben en un
              teléfono. En móvil cada producto es una tarjeta; en escritorio no cambia nada. */}
          <div className="data-table-wrap"><table className="data-table cost-table data-table--cards dt-analytic"><thead><tr><th>Producto</th><th>Variante</th><th>Cantidad</th><th>Venta bruta</th><th>Receta</th><th>Estado</th><th>Costo estimado unitario</th><th>Costo estimado total</th><th>Margen estimado</th><th>Costo real promedio</th><th>Te quedó después del costo</th><th>Diferencia</th><th>Estado costo real</th><th>Alertas</th><th>Acción</th></tr></thead><tbody>{result.items.map((item) => {
            const key = item.loyverseVariantId || item.loyverseItemId || `${item.itemName}-${item.variantName}-${item.sku}`;
            return <tr className={cards.rowClass(key)} key={key} onClick={() => cards.toggle(key)}>
              <td className="dt-card-main"><strong>{item.itemName}</strong><small>{item.variantName || 'General'} · {item.sku || 'Sin SKU'} · {item.receiptsCount} ticket(s)</small></td>
              <td className="dt-hide-m" data-label="Variante">{item.variantName || 'General'}</td>
              <td className="dt-card-keep" data-label="Cantidad">{formatQuantity(item.quantitySold)}</td>
              <td className="dt-card-keep" data-label="Venta bruta">{formatMoney(item.grossSales)}</td>
              <td data-label="Receta">{item.linkedRecipeName || 'Sin receta'}<CostingSources sources={item.costingSources} /></td>
              <td className="dt-card-keep" data-label="Estado"><RecipeStatusBadge status={item.recipeStatus} /></td>
              <td data-label="Costo estimado unitario">{formatOptionalMoney(item.estimatedUnitCost)}</td>
              <td data-label="Costo estimado total">{formatOptionalMoney(item.estimatedTotalCost)}</td>
              <td data-label="Margen estimado">{item.estimatedGrossMargin === null ? 'No disponible' : <><strong>{formatMoney(item.estimatedGrossMargin)}</strong><small>{item.estimatedGrossMarginPercent === null ? '—' : `${item.estimatedGrossMarginPercent}%`}</small></>}</td>
              <td data-label="Costo real promedio">{formatOptionalMoney(item.realTotalCost)}<CostingSources sources={item.realCostSources} /></td>
              <td className="dt-card-keep" data-label="Te quedó después del costo">{item.realAverageMargin === null ? 'No disponible' : <><strong>{formatMoney(item.realAverageMargin)}</strong><small>{item.realAverageMarginPercent === null ? '—' : `${item.realAverageMarginPercent}%`}</small></>}</td>
              <td data-label="Diferencia">{formatOptionalSignedMoney(item.plannedVsRealDifference)}</td>
              <td data-label="Estado costo real"><RealCostBadge available={item.realCostAvailable} /></td>
              <td data-label="Alertas">{item.warnings.length ? <ul className="coverage-warnings">{item.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : '—'}</td>
              <td className="dt-card-actions dt-card-keep">{item.sampleReceiptId ? <button className="text-button" disabled={isDetailLoading} onClick={(event) => { event.stopPropagation(); void openReceipt(item.sampleReceiptId!); }} type="button">Ver ticket</button> : '—'}</td>
            </tr>;
          })}</tbody></table></div>
          <div className="coverage-pagination"><span>Mostrando {result.pagination.offset + 1}–{result.pagination.offset + result.items.length} de {result.pagination.total}</span><div><button className="button button--ghost" disabled={isLoading || result.pagination.offset === 0} onClick={() => void loadEstimate(filters, Math.max(0, result.pagination.offset - PAGE_SIZE))} type="button">Anterior</button><button className="button button--ghost" disabled={isLoading || !result.pagination.hasMore} onClick={() => void loadEstimate(filters, result.pagination.offset + PAGE_SIZE)} type="button">Siguiente</button></div></div>
        </>
      ) : <div className="empty-panel"><strong>No hay ventas para este rango</strong><span>Ajusta las fechas o actualiza las ventas desde Integraciones.</span></div>}

      {(isDetailLoading || detail || detailError) && <Modal title={detail?.receiptNumber ? `Ticket ${detail.receiptNumber}` : 'Costo estimado del ticket'} description="Detalle de solo lectura; no modifica el ticket ni el inventario." isBusy={isDetailLoading} onClose={() => { setDetail(undefined); setDetailError(undefined); }}>
        {detailError && <div className="alert alert--error">{detailError}</div>}
        {isDetailLoading && <div className="inline-loading"><span className="spinner" />Calculando ticket...</div>}
        {detail && <ReceiptCostDetail detail={detail} stores={authStores} />}
      </Modal>}
    </div>
  );
}

function ReceiptCostDetail({ detail, stores }: { detail: ReceiptCostEstimateDetail; stores: AuthStore[] }) {
  return <div className="cost-detail">
    <dl className="detail-list">
      <div><dt>Fecha</dt><dd>{formatDate(detail.receiptDate, resolveStoreTimezone(stores, { storeId: detail.store?.id }))}</dd></div>
      <div><dt>Tienda</dt><dd>{detail.store?.name || detail.storeExternalId || 'Sin dato'}</dd></div>
      <div><dt>Venta</dt><dd>{formatMoney(detail.totalMoney)}</dd></div>
      <div><dt>Costo estimado</dt><dd>{formatOptionalMoney(detail.estimatedCost)}</dd></div>
      <div><dt>Margen estimado</dt><dd>{formatOptionalMoney(detail.estimatedGrossMargin)}</dd></div>
      <div><dt>Margen estimado %</dt><dd>{detail.estimatedGrossMarginPercent === null ? 'No disponible' : `${detail.estimatedGrossMarginPercent}%`}</dd></div>
      <div><dt>Costo real promedio</dt><dd>{formatOptionalMoney(detail.realAverageCost)}</dd></div>
      <div><dt>Margen real promedio</dt><dd>{formatOptionalMoney(detail.realAverageGrossMargin)}</dd></div>
    </dl>
    {!detail.isCostComplete && <div className="alert alert--error">El ticket no tiene costo completo; no se calcula margen global.</div>}
    <h3>Productos</h3>
    <div className="cost-detail-lines">{detail.lines.map((line) => <article key={line.id}><div><strong>{line.itemName}</strong><small>{line.variantName || 'General'} · {formatQuantity(line.quantity)} unidad(es)</small></div><RecipeStatusBadge status={line.recipeStatus} /><span>Estimado: {formatOptionalMoney(line.estimatedLineCost)}</span><span>Real: {formatOptionalMoney(line.realLineCost)}</span><RealCostBadge available={line.realCostAvailable} /><CostingSources sources={line.costingSources} /><CostingSources sources={line.realCostSources} />{line.warnings.length > 0 && <small>{line.warnings.join(' ')}</small>}</article>)}</div>
  </div>;
}

function SummaryCard({ label, value, help }: { label: string; value: string | number; help?: string }) { return <article><span>{label}</span><strong>{value}</strong>{help && <small className="summary-card-help">{help}</small>}</article>; }

function defaultFilters(storeId?: string): RecipeCoverageFilters {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return { dateFrom: toDateInput(from), dateTo: toDateInput(today), storeId, onlyMissingRecipe: false, onlyIncompleteCost: false };
}

function toDateInput(value: Date) { return dateToStoreISO(value); }

function formatDate(value: string | null, timeZone: string = DEFAULT_STORE_TIMEZONE) { return value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value)) : 'Sin fecha'; }
